import { cn } from "@/utils/Functions";
import { Mail, Phone, Building2 } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { PreviewCard, PreviewCardTrigger, PreviewCardPopup } from "@/components/ui/preview-card";
import { useCalendarData } from "@/hooks/calendar/useCalendarData";
import type { CalendarTask, CalendarContact } from "@/types/calendar.types";
import { IconCalendarClock, IconProgress } from "@/components/icons";

const getPriorityColor = (priority: CalendarTask["priority"]) => {
  switch (priority) {
    case "high":
      return "text-task-progress-high";
    case "medium":
      return "text-task-progress-medium";
    case "low":
      return "text-task-progress-low";
    default:
      return "text-task-progress-default";
  }
};

const formatDueDate = (task: CalendarTask) => {
  if (!task.dueDate) return "";
  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  if (task.dueDate === today) return task.dueTime ? `Today ${task.dueTime}` : "Today";
  if (task.dueDate === tomorrow) return task.dueTime ? `Tomorrow ${task.dueTime}` : "Tomorrow";

  const date = new Date(task.dueDate);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

interface TodoTaskItemProps {
  task: CalendarTask;
  onClick: () => void;
}

const ContactPreview = ({ contact }: { contact: CalendarContact }) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-3">
      <Avatar
        src={contact.avatar}
        fallback={contact.name.charAt(0)}
        className="size-10"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{contact.name}</div>
        <div className="text-xs text-muted-foreground truncate">{contact.role}</div>
      </div>
    </div>
    <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <Building2 className="size-3.5 shrink-0" />
        <span className="truncate">{contact.company}</span>
      </div>
      <div className="flex items-center gap-2">
        <Mail className="size-3.5 shrink-0" />
        <span className="truncate">{contact.email}</span>
      </div>
      <div className="flex items-center gap-2">
        <Phone className="size-3.5 shrink-0" />
        <span className="truncate">{contact.phone}</span>
      </div>
    </div>
    {contact.tags.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {contact.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
          >
            {tag}
          </span>
        ))}
      </div>
    )}
  </div>
);

export const TodoTaskItem = ({ task, onClick }: TodoTaskItemProps) => {
  const { getContactById } = useCalendarData();
  const contact = task.contactId ? getContactById(task.contactId) : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group cursor-pointer bg-neutral-100 dark:bg-transparent flex items-start gap-3 p-3 hover:dark:bg-muted/50 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
    >
      <IconProgress
        className={cn("size-5 mt-0.5 shrink-0 transition-colors", getPriorityColor(task.priority))}
        aria-label={`Task: ${task.title}, Priority: ${task.priority}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-foreground text-sm font-medium">{task.title}</div>
        <div className="flex items-center gap-2 mt-1">
          {task.dueDate && (
            <span className={cn("text-xs", "text-muted-foreground")}>
              <div className="flex items-center gap-1">
                <IconCalendarClock className="size-4" />
                {formatDueDate(task)}
              </div>
            </span>
          )}
        </div>
      </div>
      {contact && (
        <PreviewCard>
          <PreviewCardTrigger
            className="shrink-0 rounded-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Avatar
              src={contact.avatar}
              fallback={contact.name.charAt(0)}
              className="size-6"
              aria-label={contact.name}
            />
          </PreviewCardTrigger>
          <PreviewCardPopup align="start" side="left" className="w-56">
            <ContactPreview contact={contact} />
          </PreviewCardPopup>
        </PreviewCard>
      )}
    </button>
  );
};
